import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TableBody, TableContainer, TablePagination } from "@mui/material";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { ApiService, CoveragePhaseService } from "../../api";
import {
	CustomButton,
	CustomDialog,
	CustomTable,
	CustomTableHead,
	icon,
	ViewTabs,
	createNotification,
} from "../../common/components";
import {
	environmentSwitch,
	hasCompanyPermission,
	hasPermission,
	isFeatureEnabled,
	isSegFeatureEnabled,
	Permissions,
	SegFlags,
	translate,
} from "../../common/providers";
import { exportView } from "../../navigation";
import styles from "./ProjectPhases.module.css";
import { HeaderPhaseRow, PhaseRow, PhaseDialog, PhaseHistoryPanel, ImportHeritagePanel } from "./components";
import AssociatedDeliverablesPanel from "./components/associated-deliverables-panel/AssociatedDeliverablesPanel";
import { FLAGS } from "../../common/providers/feature-flag-provider";
import { usePhaseOperations } from "./hooks/usePhaseOperations";
import { usePhaseDragAndDrop } from "./hooks/usePhaseDragAndDrop";
import { usePhasePagination } from "./hooks/usePhasePagination";
import { usePhaseDialogs } from "./hooks/usePhaseDialogs";
import { exportPhaseStrategy } from "./utils/exportUtils";

const ProjectPhases = () => {
	const navigate = useNavigate();
	const [activePhases, setActivePhases] = useState([]);
	const [archivedPhases, setArchivedPhases] = useState([]);
	const [activePage, setActivePage] = useState(0);
	const [activeLimit, setActiveLimit] = useState(50);
	const [activeTotal, setActiveTotal] = useState(0);
	const [archivedPage, setArchivedPage] = useState(0);
	const [archivedLimit, setArchivedLimit] = useState(50);
	const [archivedTotal, setArchivedTotal] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedTab, setSelectedTab] = useState(0);
	const [isNew, setIsNew] = useState(true);
	const [openPhaseDialog, setOpenPhaseDialog] = useState(false);
	const [tempPhase, setTempPhase] = useState({});
	const [openHistoryPanel, setOpenHistoryPanel] = useState(false);
	const [historyPhase, setHistoryPhase] = useState({});
	const [openAssociatedPanel, setOpenAssociatedPanel] = useState(false);
	const [associatedPhaseId, setAssociatedPhaseId] = useState(null);
	const [associatedPhaseImported, setAssociatedPhaseImported] = useState(false);
	const [order, setOrder] = useState("asc");
	const [orderBy, setOrderBy] = useState("position");
	const [openImportHeritage, setOpenImportHeritage] = useState(false);
	const [importPhase, setImportPhase] = useState(null);
	const [editingPhaseId, setEditingPhaseId] = useState(null);
	const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
	const [phaseToDelete, setPhaseToDelete] = useState(null);
	const projectId = useSelector(({ context }) => context.project.id);
	const cancelTokenSourceRef = useRef(null);
	const updateCancelTokenSourceRef = useRef(null);
	const isMountedRef = useRef(true);
	const isAllowedToEdit = useMemo(
		() =>
			environmentSwitch({
				main:
					hasCompanyPermission([Permissions.PROJECT_LEADER, Permissions.PROJECT_MANAGER]) &&
					hasPermission([Permissions.PROJECT_LEADER]),
				veolia: true,
				smac: true,
			}),
		[]
	);
	const buildSortParam = useCallback(() => {
		if (!orderBy) {
			return undefined;
		}
		return `${orderBy},${order}`;
	}, [order, orderBy]);

	const {
		handleArchive,
		handleDelete,
		handleLock,
		handleSavePhase: handleSavePhaseOperation,
		handleSubmitPhaseDialog: handleSubmitPhaseDialogOperation,
	} = usePhaseOperations({
		isMountedRef,
		projectId,
		setActivePhases,
		setArchivedPhases,
		updateCancelTokenSourceRef,
	});

	const getActivePhases = useCallback(() => {
		const sortParam = buildSortParam();
		CoveragePhaseService.getAllByProjectPaginated(
			{ projectId, active: true },
			{
				page: activePage,
				limit: activeLimit,
				sort: sortParam,
			},
			cancelTokenSourceRef.current.token
		)
			.then((data) => {
				if (!isMountedRef.current) {
					return;
				}
				const items = data?.content || data?.contents || [];
				setActivePhases(items);
				setActiveTotal(data?.totalElements ?? items.length ?? 0);
			})
			.catch((err) => {
				if (!ApiService.isRequestCancellation(err?.message)) {
					console.error(err);
				}
			});
	}, [projectId, activePage, activeLimit, buildSortParam]);

	const getArchivedPhases = useCallback(() => {
		const sortParam = buildSortParam();
		CoveragePhaseService.getAllByProjectPaginated(
			{ projectId, active: false },
			{
				page: archivedPage,
				limit: archivedLimit,
				sort: sortParam,
			},
			cancelTokenSourceRef.current.token
		)
			.then((data) => {
				if (!isMountedRef.current) {
					return;
				}
				const items = data?.content || data?.contents || [];
				setArchivedPhases(items);
				setArchivedTotal(data?.totalElements ?? items.length ?? 0);
			})
			.catch((err) => {
				if (!ApiService.isRequestCancellation(err?.message)) {
					console.error(err);
				}
			});
	}, [projectId, archivedPage, archivedLimit, buildSortParam]);

	useEffect(() => {
		isMountedRef.current = true;
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		document.title = translate("project-phases.title");
		updateCancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			isMountedRef.current = false;
			ApiService.cancelTokens(cancelTokenSourceRef.current);
			ApiService.cancelTokens(updateCancelTokenSourceRef.current);
		};
	}, []);
	useEffect(() => {
		setIsLoading(true);
		const promises = [getActivePhases()];
		promises.push(getArchivedPhases());
		Promise.all(promises).finally(() => {
			if (isMountedRef.current) {
				setIsLoading(false);
			}
		});
	}, [getActivePhases, getArchivedPhases]);

	const handleSort = useCallback(
		(property) => {
			const isAsc = orderBy === property && order === "asc";
			setOrder(isAsc ? "desc" : "asc");
			setOrderBy(property);
			if (selectedTab === 0) {
				setActivePage(0);
			} else {
				setArchivedPage(0);
			}
		},
		[orderBy, order, selectedTab, setActivePage, setArchivedPage]
	);

	const {
		handleClickAdd,
		handleClickEdit,
		handleClosePhaseDialog,
		handleClickHistory,
		handleCloseHistoryPanel,
		handleOpenAssociatedPanel,
		handleCloseAssociatedPanel,
		handleOpenImportHeritage,
		handleCloseImportHeritage,
	} = usePhaseDialogs({
		setOpenPhaseDialog,
		setIsNew,
		setTempPhase,
		setEditingPhaseId,
		setOpenHistoryPanel,
		setHistoryPhase,
		setOpenAssociatedPanel,
		setAssociatedPhaseId,
		setAssociatedPhaseImported,
		setOpenImportHeritage,
		setImportPhase,
		editingPhaseId,
	});

	const handleClickDelete = useCallback(
		(phase) => {
			setPhaseToDelete(phase);
			setDeleteConfirmationOpen(true);
		},
		[setPhaseToDelete, setDeleteConfirmationOpen]
	);

	const handleConfirmDelete = useCallback(() => {
		if (phaseToDelete) {
			handleDelete(phaseToDelete.id);
		}
		setDeleteConfirmationOpen(false);
		setPhaseToDelete(null);
	}, [phaseToDelete, handleDelete, setDeleteConfirmationOpen, setPhaseToDelete]);

	const handleCancelDelete = useCallback(() => {
		setDeleteConfirmationOpen(false);
		setPhaseToDelete(null);
	}, [setDeleteConfirmationOpen, setPhaseToDelete]);

	const handleExport = useCallback(
		(phase) => {
			exportPhaseStrategy(projectId, phase, cancelTokenSourceRef.current.token);
		},
		[projectId]
	);

	const handleClickArchive = useCallback(
		(phase) => {
			handleArchive(phase, selectedTab === 0);
		},
		[handleArchive, selectedTab]
	);
	const handleClickExport = useCallback(
		(phase) => {
			handleExport(phase);
		},
		[handleExport]
	);
	const handleSavePhase = useCallback(
		(editedPhase) => {
			handleSavePhaseOperation(editedPhase, selectedTab);
			setEditingPhaseId(null);
		},
		[handleSavePhaseOperation, selectedTab]
	);
	const handleStartInlineEdit = useCallback(
		(phaseId) => {
			if (openPhaseDialog) {
				createNotification({
					type: "warning",
					message: translate("project-phases.edit.in-progress"),
				});
				return false;
			}
			if (editingPhaseId !== null && editingPhaseId !== phaseId) {
				createNotification({
					type: "warning",
					message: translate("project-phases.edit.in-progress"),
				});
				return false;
			}
			setEditingPhaseId(phaseId);
			return true;
		},
		[editingPhaseId, openPhaseDialog]
	);
	const handleEndInlineEdit = useCallback(() => {
		setEditingPhaseId(null);
	}, []);
	const handleCancelEdit = () => {
		setEditingPhaseId(null);
	};
	const handleClickLock = useCallback(
		(phase) => {
			handleLock(phase, selectedTab);
		},
		[handleLock, selectedTab]
	);
	const handleSubmitPhaseDialog = useCallback(
		(newPhase) => {
			handleSubmitPhaseDialogOperation(newPhase, isNew, setOpenPhaseDialog, setTempPhase, setEditingPhaseId);
		},
		[handleSubmitPhaseDialogOperation, isNew, setOpenPhaseDialog, setTempPhase, setEditingPhaseId]
	);

	const { handleDragEnd } = usePhaseDragAndDrop({
		selectedTab,
		activePhases,
		archivedPhases,
		projectId,
		getActivePhases,
		getArchivedPhases,
		updateCancelTokenSourceRef,
		setActivePhases,
		setArchivedPhases,
	});

	const isProjectPhasesEnabled =
		isFeatureEnabled(FLAGS.COVERAGE_PHASE_LOCK) && isSegFeatureEnabled(SegFlags.COVERAGE_PHASE_LOCK);

	const tabs = useMemo(
		() => [
			{ label: translate("project-phases.tabs.active") },
			...(isProjectPhasesEnabled ? [{ label: translate("project-phases.tabs.archived") }] : []),
		],
		[isProjectPhasesEnabled]
	);

	useEffect(() => {
		if (!isProjectPhasesEnabled && selectedTab === 1) {
			setSelectedTab(0);
		}
	}, [isProjectPhasesEnabled, selectedTab]);

	const currentPhases = useMemo(
		() => (selectedTab === 0 ? activePhases : archivedPhases),
		[selectedTab, activePhases, archivedPhases]
	);

	const { page, limit, total, handleChangePage, handleChangeLimitPerPage } = usePhasePagination({
		selectedTab,
		activePage,
		activeLimit,
		activeTotal,
		archivedPage,
		archivedLimit,
		archivedTotal,
		setActivePage,
		setActiveLimit,
		setArchivedPage,
		setArchivedLimit,
	});

	return (
		<>
			<div className={styles.headerSection}>
				<div className={styles.titleSection}>
					<div className={styles.pageTitle}>{translate("project-phases.page-title")}</div>
					<div className={styles.subtitle}>{translate("project-phases.subtitle")}</div>
				</div>
			</div>
			<div className={styles.tabsHeader}>
				<div className={styles.tabsWrapper}>
					<ViewTabs
						noShadow
						selectedTab={selectedTab}
						tabClassName={styles.customTab}
						tabs={tabs}
						onChange={(_, idx) => setSelectedTab(idx)}
					/>
				</div>
				{!isLoading && isAllowedToEdit && selectedTab === 0 && (
					<div className={styles.actionWrapper}>
						<CustomButton
							color="primary"
							size="md"
							startIcon={icon.faPlus}
							variant="contained"
							onClick={handleClickAdd}
						>
							{translate("project-phases.button.new-phase")}
						</CustomButton>
					</div>
				)}
			</div>
			<div className={styles.mainContainer}>
				<div className={styles.mainContainer__list}>
					<TableContainer className={styles.dragover} component="div">
						<CustomTable stickyHeader>
							<CustomTableHead>
								<HeaderPhaseRow
									isArchivedTab={selectedTab === 1}
									isEditable={isAllowedToEdit}
									order={order}
									orderBy={orderBy}
									onSort={handleSort}
								/>
							</CustomTableHead>
							<DragDropContext onDragEnd={handleDragEnd}>
								<Droppable droppableId={`phases-${selectedTab}`} type="phase">
									{(dropProvided) => (
										<TableBody ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
											{currentPhases.map((phase, index) => (
												<Draggable
													key={phase.id}
													draggableId={phase.id.toString()}
													index={index}
													isDragDisabled={!isAllowedToEdit || phase.locked}
													type="phase"
												>
													{(dragProvided, dragSnapshot) => (
														<PhaseRow
															ref={dragProvided.innerRef}
															isProjectPhases
															className={
																dragSnapshot.isDragging ? styles.draggingRow : ""
															}
															editingPhaseId={editingPhaseId}
															isArchivedTab={selectedTab === 1}
															isEditable={isAllowedToEdit}
															phase={phase}
															projectId={projectId}
															provided={dragProvided}
															rowOrder={index}
															snapshot={dragSnapshot}
															onCancel={handleCancelEdit}
															onClickArchive={handleClickArchive}
															onClickDelete={handleClickDelete}
															onClickEdit={handleClickEdit}
															onClickExport={handleClickExport}
															onClickHistory={handleClickHistory}
															onClickImportHeritage={handleOpenImportHeritage}
															onClickLock={handleClickLock}
															onClickOpenAssociated={handleOpenAssociatedPanel}
															onEndInlineEdit={handleEndInlineEdit}
															onSave={handleSavePhase}
															onStartInlineEdit={handleStartInlineEdit}
														/>
													)}
												</Draggable>
											))}
											{dropProvided.placeholder}
										</TableBody>
									)}
								</Droppable>
							</DragDropContext>
						</CustomTable>
					</TableContainer>
				</div>
				{total > 50 && (
					<TablePagination
						classes={{ root: styles.table__pagination }}
						component="div"
						count={total || 0}
						page={page}
						rowsPerPage={limit}
						rowsPerPageOptions={[50, 100]}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeLimitPerPage}
					/>
				)}
			</div>
			<PhaseDialog
				isProjectPhases
				isNew={isNew}
				open={openPhaseDialog}
				phase={tempPhase}
				projectId={projectId}
				onClose={handleClosePhaseDialog}
				onSubmit={handleSubmitPhaseDialog}
			/>
			<PhaseHistoryPanel
				open={openHistoryPanel}
				phase={historyPhase}
				projectId={projectId}
				onClose={handleCloseHistoryPanel}
			/>
			<AssociatedDeliverablesPanel
				imported={associatedPhaseImported}
				open={openAssociatedPanel}
				phaseId={associatedPhaseId}
				onClose={handleCloseAssociatedPanel}
				onOpenList={(phaseId) => {
					if (phaseId) {
						navigate(`/projects/${projectId}/deliverables/list`, {
							state: {
								filters: {
									phases: {
										is: true,
										matchAll: false,
										elements: [phaseId],
									},
								},
							},
						});
					}
					handleCloseAssociatedPanel();
				}}
			/>
			<ImportHeritagePanel
				open={openImportHeritage}
				phase={importPhase}
				onClose={handleCloseImportHeritage}
				onImportSuccess={() => {
					getActivePhases();
					getArchivedPhases();
				}}
			/>
			<CustomDialog
				cancelLabel={translate("common.cancel")}
				iconColor="red"
				iconModel={icon.faExclamationTriangle}
				open={deleteConfirmationOpen}
				submitLabel={translate("common.confirm")}
				subTitle={translate("project-phases.confirm-delete-message")}
				title={translate("project-phases.confirm-delete-title")}
				onClose={handleCancelDelete}
				onSubmit={handleConfirmDelete}
			/>
		</>
	);
};

export default exportView({
	path: "/projects/:projectId/project-phases",
	localesPath: "/project-phases/locales",
	component: ProjectPhases,
	segFlag: SegFlags.DELIVERABLES,
});
